import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, MapPin, Settings } from "react-feather";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AddEdit_StateMaster = () => {
  const API_URL_SAVE = "StateMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/StateMaster/Id";
  
  const breadCrumbTitle = "State Master";
  const breadcrumbItem = "State Master";

  const [state, setState] = useState({
    id: 0,
    formData: { 
      Name: "", 
      StateCode: ""
    }
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;

    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);


  // Validation Schema
  const validationSchema = Yup.object({
    Name: Yup.string()
      .required("State name is required")
      .min(2, "State name must be at least 2 characters"),
    StateCode: Yup.string()
      .required("State code is required")
      .min(2, "State code must be at least 2 characters")
      .max(10, "State code must be less than 10 characters"),
  });

  const handleSubmit = async (values) => {
   const obj = JSON.parse(localStorage.getItem("user"));
       let formData = new FormData();
       formData.append("Name", values.Name || "");
       formData.append("StateCode", values.StateCode || "");
       formData.append("UserId", obj===null || obj===undefined ? 0 : obj.uid);
   
       Fn_AddEditData(
         dispatch,
         setState,
         { arguList: { id: state.id, formData } },
         API_URL_SAVE,
         true,
         "memberid",
         navigate,
         "/StateMaster"
       );
  };

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header" style={{padding: '12px 20px'}}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={18} color="#6D68CB" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold">State Master</h5>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="master-form-card-body">
                <Formik 
                  initialValues={{
                    Name: state.formData.Name || "",
                    StateCode: state.formData.StateCode || ""
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="Name" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            State Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              innerRef={nameRef}
                              className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                              type="text"
                              name="Name"
                              placeholder="Enter state name"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Name" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the name of the state
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="StateCode" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            State Code <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.StateCode && touched.StateCode ? 'is-invalid' : ''}`}
                              type="text"
                              name="StateCode"
                              placeholder="Enter state code"
                              style={{ textTransform: 'uppercase' }}
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="StateCode" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the state code (e.g., MH, UP, DL)
                          </div>
                        </div>
                      </div>

                      <div className="master-form-actions mt-3">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate("/StateMaster")}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            {state.id > 0 ? 'Update State' : 'Save State'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_StateMaster;